/**
 * Route guard for authenticated and role-based access.
 *
 * - Shows a loading spinner while the initial auth check runs.
 * - Redirects unauthenticated users to /login (preserving the
 *   intended URL for post-login redirect).
 * - Optionally requires the admin role.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  // Still verifying stored token — show loading indicator
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-400 text-sm">Loading&hellip;</div>
      </div>
    );
  }

  // Not authenticated — redirect to login, preserving intended URL
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Requires admin but user is viewer — redirect to home
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}
